import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { shopifyApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Public callback page for the Shopify OAuth flow.
 *
 * Shopify redirects the user to /auth/shopify/callback with:
 *   ?code=…&shop=…&state=…&hmac=…&timestamp=…
 *
 * We POST those query params to /api/shopify/oauth/callback (authenticated
 * with the user's existing Owntune session cookie/token), then forward back
 * to the Stock Integration wizard.
 */
export default function ShopifyOAuthCallbackPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { user, loading } = useAuth();
    const [phase, setPhase] = useState('exchanging'); // exchanging | success | error
    const [message, setMessage] = useState('Completing Shopify connection…');
    const ranRef = useRef(false);

    useEffect(() => {
        if (loading) return;
        if (!user) {
            // Not logged in → redirect to login, preserve the callback URL
            const target = `${window.location.pathname}${window.location.search}`;
            navigate(`/login?next=${encodeURIComponent(target)}`, { replace: true });
            return;
        }
        if (ranRef.current) return;
        ranRef.current = true;

        const code = params.get('code');
        const shop = params.get('shop');
        const state = params.get('state');
        const hmac = params.get('hmac');
        const timestamp = params.get('timestamp');
        const host = params.get('host');

        if (!code || !shop || !state) {
            setPhase('error');
            setMessage('Missing OAuth parameters from Shopify (code / shop / state).');
            return;
        }

        (async () => {
            try {
                const res = await shopifyApi.oauthCallback({
                    code, shop, state, hmac, timestamp, host
                });
                if (res.data?.success) {
                    setPhase('success');
                    setMessage(`Connected to ${res.data.shop_domain}`);
                    sessionStorage.removeItem('shopify_oauth_shop');
                    setTimeout(() => navigate('/catalogue/integrations?connected=shopify', { replace: true }), 2000);
                } else {
                    setPhase('error');
                    setMessage('Shopify did not confirm the connection.');
                }
            } catch (e) {
                const detail = e.response?.data?.detail || 'Could not complete Shopify connection.';
                setPhase('error');
                if (/invalid or expired oauth state/i.test(detail)) {
                    setMessage(
                        'Your Shopify connection session expired or was started in a different ' +
                        'browser tab. Please click "Connect Shopify" again from the wizard — ' +
                        'no need to re-approve on Shopify if the app is already installed.'
                    );
                    setTimeout(() => navigate('/catalogue/integrations', { replace: true }), 4000);
                } else {
                    setMessage(detail);
                }
            }
        })();
    }, [params, user, loading, navigate]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50/40 to-white p-6" data-testid="shopify-oauth-callback">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 max-w-md w-full p-10 text-center space-y-5">
                {/* Shopify logo always at top */}
                <div className="mx-auto w-14 h-14 rounded-2xl bg-[#5E8E3E]/10 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="h-9 w-9" aria-hidden="true">
                        <path
                            fill="#95BF47"
                            d="M15.32 4.72c-.06-.04-.12-.06-.18-.06-.06-.06-1.32-.12-1.32-.12s-1.02-1.02-1.14-1.14c-.12-.12-.34-.08-.42-.06l-.58.18c-.06-.18-.16-.4-.3-.62-.42-.8-1.06-1.22-1.78-1.22h-.04c-.06 0-.1.02-.16.04C9.34 1.66 9.26 1.5 9.16 1.4c-.46-.5-1.16-.74-2-.7-1.66.04-3.32 1.24-4.66 3.36C1.56 5.62.92 7.42.78 8.84.04 11.18.62 12.18.74 12.34l3.5 1.36 1.34 9.74 6.62-1.42-2.88-15.3z"
                        />
                        <path
                            fill="#5E8E3E"
                            d="M15.14 4.66c-.06.02-.12.04-.18.06l-1 .3c-.04-.5-.16-1.18-.4-1.86-.78.14-1.56.74-2.06 1.46.36-.1.74-.22 1.16-.34.32-.84.84-1.74 1.78-1.74h.06c.28 0 .56.1.74.4-.4.06-.66.34-.66.74-.02.4.18.74.56.74h.04l-.04.24z"
                        />
                        <path
                            fill="#FFF"
                            d="M14.94 4.66l-1.04 5.5c-.18-.06-.46-.18-.66-.3-.18-.1-.5-.34-.84-.66-.34-.32-.6-.6-.84-1.04-.24-.42-.4-.96-.4-1.6 0-.4.06-.78.16-1.08.18-.62.5-1.04.74-1.32.36-.42.74-.7 1.16-.86.4-.16.78-.18 1.06-.16.34.04.62.18.66.5z"
                        />
                    </svg>
                </div>
                {phase === 'exchanging' && (
                    <>
                        <Loader2 className="h-8 w-8 mx-auto text-[#5E8E3E] animate-spin" />
                        <h1 className="text-lg font-semibold text-gray-900">Connecting Shopify…</h1>
                        <p className="text-sm text-gray-500">{message}</p>
                    </>
                )}
                {phase === 'success' && (
                    <>
                        <div className="mx-auto w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-200 animate-in zoom-in duration-300">
                            <CheckCircle2 className="h-7 w-7 text-white" />
                        </div>
                        <h1 className="text-xl font-semibold text-gray-900">Successfully connected to Shopify</h1>
                        <p className="text-sm text-gray-700">{message}</p>
                        <p className="text-xs text-gray-400 pt-1">Returning to Stock Integration…</p>
                    </>
                )}
                {phase === 'error' && (
                    <>
                        <AlertCircle className="h-10 w-10 mx-auto text-red-500" />
                        <h1 className="text-lg font-semibold text-gray-900">Connection failed</h1>
                        <p className="text-sm text-gray-600">{message}</p>
                        <button
                            onClick={() => navigate('/catalogue/integrations', { replace: true })}
                            className="mt-2 text-sm text-indigo-600 hover:underline"
                            data-testid="btn-back-to-wizard"
                        >
                            Back to Stock Integration
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
